import { Box, Button, FormHelp, Heading, Paragraph, TextButton } from '@zillow/constellation-10';

import { LoadingHeading, LoadingParagraph } from '@zg-rentals/loading-components';

import ClaimListingCard from '../../../ClaimListingCard';

import numberUtils from '../../../../utils/numberUtils';
import stringHelper from '../../../../utils/stringHelper';

import type { UserClaimPropertyLookupListingDto } from '@zg-rentals/ts-rental-manager-api-client';

const { pluralize } = stringHelper;
const { formatPhoneNumberInput } = numberUtils;

export type SearchResultsProps = {
  contactMethod: 'email' | 'phone' | '';
  contactValue: string;
  listings: Array<UserClaimPropertyLookupListingDto>;
  listingCount: number;
  onAddContactMethod: () => void;
  onChooseAnotherContactMethod: () => void;
  errorMessage: string;
  isPropertyLookupFetching: boolean;
};

const SearchResults = ({
  contactMethod,
  contactValue,
  listings,
  listingCount,
  onAddContactMethod,
  onChooseAnotherContactMethod,
  errorMessage,
  isPropertyLookupFetching,
}: SearchResultsProps) => {
  const handleSearchAnotherContact = () => {
    onChooseAnotherContactMethod();
  };

  const handleAddContactMethod = () => {
    onAddContactMethod();
  };

  const renderListings = () => (
    <>
      <Box css={{ textAlign: 'center', marginBottom: '200' }}>
        <LoadingHeading
          level={3}
          textStyle="body-lg-bold"
          isLoading={isPropertyLookupFetching}
          loadingWidth="70%"
        >{`We found ${pluralize(listingCount, 'listing', 'listings')}`}</LoadingHeading>
        <LoadingParagraph
          css={{ marginBottom: '200' }}
          className="fs-mask"
          isLoading={isPropertyLookupFetching}
          loadingWidth="30%"
        >
          {contactMethod === 'phone' ? formatPhoneNumberInput(contactValue) : contactValue}
        </LoadingParagraph>
        <LoadingParagraph css={{ marginBottom: '200' }} isLoading={isPropertyLookupFetching} loadingWidth="70%">
          <b>Not your listings? </b>
          Try another <TextButton onClick={onChooseAnotherContactMethod}>contact</TextButton>
        </LoadingParagraph>
      </Box>
      <div>
        {listings.map((listing, index) => (
          <ClaimListingCard
            key={`ClaimListingCard-${isPropertyLookupFetching ? `skeleton-${index}` : listing?.listingAlias}`}
            listingData={listing}
            highlightEmail={contactMethod === 'email'}
            highlightPhone={contactMethod === 'phone'}
            loading={isPropertyLookupFetching}
          />
        ))}
      </div>
      {errorMessage && (
        <FormHelp error css={{ marginTop: '200' }}>
          {errorMessage}
        </FormHelp>
      )}
    </>
  );

  const renderNoListingsFound = () => (
    <>
      <Heading
        level={4}
        textStyle="heading-xs"
        className="fs-mask"
        css={{ textAlign: 'center', marginTop: '1200', marginBottom: '400' }}
      >
        {`We didn’t find any listings for ${
          contactMethod === 'phone' ? formatPhoneNumberInput(contactValue) : contactValue
        }`}
      </Heading>
      <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Button tone="brand" emphasis="filled" size="lg" onClick={handleSearchAnotherContact}>
          Try another contact method
        </Button>
        <Box
          css={{
            border: '1px solid {colors.yellow.300}',
            width: '100%',
            maxWidth: '142px',
            margin: '{spacing.600} auto {spacing.800} auto',
          }}
        />
        <Paragraph className="fs-mask" css={{ textAlign: 'center', maxWidth: '450px', marginBottom: '200' }}>
          {`If you’ll use ${
            contactMethod === 'phone' ? formatPhoneNumberInput(contactValue) : contactValue
          } for future listings, add it to your contact methods now to save time later.`}
        </Paragraph>
        <Button tone="brand" emphasis="outlined" size="lg" onClick={handleAddContactMethod} disabled={!!errorMessage}>
          {contactMethod === 'phone' ? 'Add phone number' : 'Add email'}
        </Button>
        {!!errorMessage && (
          <Box css={{ marginBlock: '200' }}>
            <FormHelp error={true}>{errorMessage}</FormHelp>
          </Box>
        )}
      </Box>
    </>
  );

  return listingCount > 0 ? renderListings() : renderNoListingsFound();
};

export default SearchResults;
